// UserAuthContext.js
import { apiUrl } from './config/api';
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './components/SellerOrderAlert.css';
import { clearApiCache } from './utils/apiRequestCache';

export const UserAuthContext = createContext();
const LOCATION_STORAGE_KEY = 'eulua_delivery_location';
const EMPTY_LOCATION = { country: 'Unknown', city: '', region: '', latitude: null, longitude: null };

const hasKnownLocation = (value) => Boolean(value?.country && value.country !== 'Unknown');
const profileLocation = (userData) => ({
  country: userData.country,
  city: userData.city || '',
  region: userData.region || '',
  latitude: userData.latitude !== null && userData.latitude !== '' && Number.isFinite(Number(userData.latitude)) ? Number(userData.latitude) : null,
  longitude: userData.longitude !== null && userData.longitude !== '' && Number.isFinite(Number(userData.longitude)) ? Number(userData.longitude) : null,
  source: 'profile',
});

const readStoredLocation = () => {
  try {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY) || localStorage.getItem('location');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.country) return null;
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(parsed));
    localStorage.removeItem('location');
    return parsed;
  } catch {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    localStorage.removeItem('location');
    return null;
  }
};

const clearStoredAuth = () => {
  clearApiCache();
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

const decodeTokenPayload = (token) => {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;
  const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  try {
    return JSON.parse(decodeURIComponent(
      atob(padded)
        .split('')
        .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    ));
  } catch {
    return null;
  }
};

const hasCurrentTokenContract = (payload) => {
  const audiences = Array.isArray(payload?.aud) ? payload.aud : [payload?.aud];
  return payload?.iss === 'eulua-api' && audiences.includes('eulua-web');
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(() => readStoredLocation() || EMPTY_LOCATION);
  const [locationReady, setLocationReady] = useState(false);
  const [approximateLocationIssue, setApproximateLocationIssue] = useState('');
  const [mobileLocationResolved, setMobileLocationResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const approximateLocationAttempted = useRef(false);
  const mobileGeolocationAttempted = useRef(false);
  const orderAlertedUser = useRef(null);
  const navigate = useNavigate();

  // Matches the breakpoint the header already uses to switch into its mobile layout.
  const isMobileViewport = () => {
    try {
      return window.matchMedia('(max-width: 900px)').matches;
    } catch {
      return false;
    }
  };

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/refresh-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to refresh token');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      return true;
    } catch {
      return false;
    }
  }, []);

  const getValidAccessToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    const payload = decodeTokenPayload(token);
    const expiresAt = Number(payload?.exp || 0);
    const now = Math.floor(Date.now() / 1000);
    if (token && hasCurrentTokenContract(payload) && expiresAt > now + 5) return token;
    const refreshed = await refreshAccessToken();
    return refreshed ? localStorage.getItem('token') : null;
  }, [refreshAccessToken]);

  const checkTokenValidity = useCallback(async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) {
      clearStoredAuth();
      setUser(null);
      setLoading(false);
      return;
    }

    const tokenPayload = decodeTokenPayload(token);
    if (!hasCurrentTokenContract(tokenPayload)) {
      clearStoredAuth();
      setUser(null);
      setLoading(false);
      return;
    }

    if (Number(tokenPayload.exp || 0) <= Math.floor(Date.now() / 1000)) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        clearStoredAuth();
        setUser(null);
      }
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/verify-token'), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data.valid) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          clearStoredAuth();
          setUser(null);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } else {
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setLoading(false);
      }
    } catch {
      clearStoredAuth();
      setUser(null);
      setLoading(false);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    checkTokenValidity();

    const interval = setInterval(checkTokenValidity, 30 * 60 * 1000); // Check every 30 minutes
    return () => clearInterval(interval);
  }, [checkTokenValidity]);

  useEffect(() => {
    const canAccessSellerSales = Number(user?.account_type) === 1
      || (user?.seller_access === true && ['owner', 'admin', 'seller'].includes(user?.seller_role));
    if (loading || !canAccessSellerSales) {
      if (!user) orderAlertedUser.current = null;
      return undefined;
    }
    const userId = user.id || user.userid || user.idusers || user.email;
    if (orderAlertedUser.current === userId) return undefined;
    orderAlertedUser.current = userId;
    const controller = new AbortController();
    const loadOrderAlerts = async () => {
      try {
        const response = await fetch(apiUrl('/api/seller/orders/alerts'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal,
        });
        if (!response.ok) return;
        const alerts = await response.json();
        if (Number(alerts.total) < 1) return;
        const details = [
          Number(alerts.pendingDecisions) > 0
            ? `${alerts.pendingDecisions} awaiting Accept/Reject` : '',
          Number(alerts.pendingReceipts) > 0
            ? `${alerts.pendingReceipts} receipt${Number(alerts.pendingReceipts) === 1 ? '' : 's'} to review` : '',
        ].filter(Boolean).join(' · ');
        const openOrders = () => navigate('/sell/dashboard');
        toast.success(
          <div className="seller-order-alert" onClick={openOrders} role="link" tabIndex={0}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openOrders(); }}>
            <strong>Orders need your attention</strong>
            <span>{details}</span>
            <button type="button" onClick={(event) => { event.stopPropagation(); openOrders(); }}>Check Orders</button>
          </div>,
          {
            toastId: `seller-order-alert-${userId}`,
            autoClose: 8000,
            closeOnClick: true,
            onClick: openOrders,
          }
        );
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Could not load seller order alerts:', error);
      }
    };
    loadOrderAlerts();
    return () => controller.abort();
  }, [loading, navigate, user]);

  useEffect(() => {
    if (loading) return undefined;

    if (user?.country && (location.source === 'approximate' || !hasKnownLocation(location))) {
      const nextLocation = profileLocation(user);
      setLocation(nextLocation);
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(nextLocation));
      setLocationReady(true);
      return undefined;
    }

    if (hasKnownLocation(location)) {
      setLocationReady(true);
      return undefined;
    }
    if (approximateLocationAttempted.current) {
      setLocationReady(true);
      return undefined;
    }
    approximateLocationAttempted.current = true;
    const controller = new AbortController();
    const detectApproximateLocation = async () => {
      try {
        const response = await fetch(apiUrl('/api/locations/approximate'), { signal: controller.signal });
        const data = await response.json();
        if (response.status === 429 && data.code === 'IP_GEOLOCATION_LIMIT_REACHED') {
          const message = 'We could not estimate your location because the daily limit was reached. Use “Use my current location” to enable precise location and improve nearby results.';
          setApproximateLocationIssue(message);
          toast.info(message, { toastId: 'approximate-location-limit', autoClose: 8000 });
          return;
        }
        if (!response.ok || !hasKnownLocation(data.location)) return;
        const nextLocation = { ...data.location, source: 'approximate' };
        setLocation(nextLocation);
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(nextLocation));
        localStorage.removeItem('location');
        setApproximateLocationIssue('');
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Could not detect approximate country:', error);
      } finally {
        if (!controller.signal.aborted) setLocationReady(true);
      }
    };
    detectApproximateLocation();
    return () => controller.abort();
  }, [loading, location, user]);

  // On phones, an IP-based guess is often too coarse (or fails outright on carrier/VPN
  // networks). Once the approximate lookup has settled, try the device's own geolocation
  // silently before giving up and asking the user to type an address.
  useEffect(() => {
    if (loading || !locationReady) return undefined;
    if (hasKnownLocation(location) || !isMobileViewport() || !navigator.geolocation) {
      setMobileLocationResolved(true);
      return undefined;
    }
    if (mobileGeolocationAttempted.current) return undefined;
    mobileGeolocationAttempted.current = true;

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const response = await fetch(apiUrl(`/api/locations/reverse?latitude=${encodeURIComponent(coords.latitude)}&longitude=${encodeURIComponent(coords.longitude)}`));
          const data = await response.json();
          if (!cancelled && response.ok && data.location) {
            await updateUserLocation(data.location);
          }
        } catch (error) {
          console.error('Could not resolve device location:', error);
        } finally {
          if (!cancelled) setMobileLocationResolved(true);
        }
      },
      () => {
        if (!cancelled) setMobileLocationResolved(true);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
    return () => {
      cancelled = true;
    };
  }, [loading, locationReady, location]);

  const login = (userData, token) => {
    clearStoredAuth();
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    const savedLocation = readStoredLocation();
    if (savedLocation && savedLocation.source !== 'approximate') {
      setLocation(savedLocation);
    } else if (userData.country) {
      const nextLocation = profileLocation(userData);
      setLocation(nextLocation);
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(nextLocation));
    } else if (savedLocation) {
      setLocation(savedLocation);
    }
  };

  const logout = () => {
    fetch(apiUrl('/api/logout'), { method: 'POST', credentials: 'include' }).catch(() => {});
    clearStoredAuth();
    setUser(null);
    navigate('/', { replace: true });
  };

  const updateCurrentUser = (updates) => {
    setUser((current) => {
      const updated = { ...(current || {}), ...updates };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  // Save the selected delivery location locally and, when signed in, on the account.
  const updateUserLocation = async (newLocation) => {
    // City/region come strictly from the newly picked location: a suggestion for a
    // state or country has no city/region of its own, and must clear the old ones
    // instead of silently keeping a mismatched city/state from a previous pick.
    const updatedLocation = {
      country: newLocation.country || location.country || 'Unknown',
      city: newLocation.city || '',
      region: newLocation.region || '',
      latitude: newLocation.latitude !== null && newLocation.latitude !== '' && Number.isFinite(Number(newLocation.latitude)) ? Number(newLocation.latitude) : null,
      longitude: newLocation.longitude !== null && newLocation.longitude !== '' && Number.isFinite(Number(newLocation.longitude)) ? Number(newLocation.longitude) : null,
      source: newLocation.source === 'approximate' ? 'approximate' : 'selected',
    };

    setLocation(updatedLocation);
    setApproximateLocationIssue('');
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(updatedLocation));
    localStorage.removeItem('location');
    const token = localStorage.getItem('token');
    if (user && !loading && token) {
      try {
        const response = await fetch(apiUrl('/api/update-location'), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(updatedLocation),
        });
        if (!response.ok) throw new Error('Could not save location');
      } catch (error) {
        console.error('Could not sync location to the profile:', error);
      }
    }
  };

  return (
    <UserAuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateCurrentUser,
      updateUserLocation,
      location,
      locationReady,
      mobileLocationResolved,
      approximateLocationIssue,
      getValidAccessToken,
      refreshAccessToken,
    }}>
      {children}
    </UserAuthContext.Provider>
  );
};
